/**
 * NeoCoreBitacoraOfacPage — Bitácora consolidada de cotejos OFAC/listas.
 *
 * Cumplimiento IVE: registro histórico de TODAS las consultas contra listas
 * (SDN/NON-SDN/DPL/UN), filtrable por fecha/resultado/búsqueda, con export a
 * PDF y Excel con encabezado institucional (según normativa).
 *
 * Datos: RPC ofac_bitacora_listar (0203). Export: exportReport (task #143).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, ShieldWarning, Shield, FilePdf, MicrosoftExcelLogo, ArrowClockwise } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { exportarPDF, exportarExcel } from '@/lib/exportReport'

type Hit = { name: string | null; score: number; source: string | null; category: string | null }
type Row = {
  id: string; created_at: string; nombre_consultado: string; documento: string | null
  fuentes: string[]; min_score: number; resultado: string; score_max: number | null
  total_hits: number; hits: Hit[]; origen: string; decision: string | null
  decision_nota: string | null; neo_id: string | null; ofac_estado: string | null
  consultado_por_email: string | null
}

const RES: Record<string, { label: string; color: string; Icon: typeof Shield }> = {
  sin_coincidencia: { label: 'Sin coincidencia', color: '#0F8A57', Icon: ShieldCheck },
  posible_coincidencia: { label: 'Posible coincidencia', color: '#C0392B', Icon: ShieldWarning },
  error: { label: 'Error', color: '#B45309', Icon: ShieldWarning },
}
const ORIGEN: Record<string, string> = { inscripcion: 'Automático (inscripción)', manual: 'Manual', reconsulta: 'Re-consulta' }

function fechaHora(iso: string) {
  return new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function NeoCoreBitacoraOfacPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [resultado, setResultado] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [exporting, setExporting] = useState(false)
  const [batch, setBatch] = useState<{ running: boolean; msg: string | null }>({ running: false, msg: null })

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.rpc('ofac_bitacora_listar', {
      p_desde: desde || null, p_hasta: hasta || null,
      p_resultado: resultado || null, p_busqueda: busqueda.trim() || null,
    })
    const arr = Array.isArray(data) ? (data as Row[]) : []
    setRows(arr)
    setLoading(false)
  }, [desde, hasta, resultado, busqueda])
  useEffect(() => { load() }, [load])

  const stats = useMemo(() => ({
    total: rows.length,
    hits: rows.filter((r) => r.resultado === 'posible_coincidencia').length,
    limpias: rows.filter((r) => r.resultado === 'sin_coincidencia').length,
  }), [rows])

  const exportData = () => {
    const headers = ['Fecha y hora', 'NeoID', 'Nombre consultado', 'Documento', 'Listas', 'Umbral', 'Resultado', 'Score máx', 'Coincid.', 'Decisión', 'Consultado por']
    const data = rows.map((r) => [
      fechaHora(r.created_at), r.neo_id ?? '—', r.nombre_consultado, r.documento ?? '—',
      (r.fuentes ?? []).join(' · '), String(r.min_score),
      RES[r.resultado]?.label ?? r.resultado, r.score_max != null ? String(r.score_max) : '—',
      String(r.total_hits), r.decision ? r.decision : (r.resultado === 'posible_coincidencia' ? 'pendiente' : '—'),
      r.consultado_por_email ?? ORIGEN[r.origen] ?? r.origen,
    ])
    const subt = `${desde || 'inicio'} al ${hasta || 'hoy'} · ${rows.length} consultas`
    return { headers, data, subt }
  }

  const correrBatch = async (grupo: 'activos' | 'prospectos') => {
    const label = grupo === 'activos' ? 'los asociados ACTIVOS' : 'los PROSPECTOS'
    if (!window.confirm(`¿Re-cotejar contra listas a ${label}?\n\nGenera una consulta por cada uno y deja bitácora. Puede tardar unos segundos.`)) return
    setBatch({ running: true, msg: null })
    try {
      const { data, error } = await supabase.functions.invoke('ofac-screen-batch', { body: { grupo, origen: 'batch' } })
      if (error) throw error
      if (data?.error) throw new Error(data.error === 'ofac_api_key_no_configurada' ? 'Falta configurar el secret OFAC_API_KEY.' : data.error)
      setBatch({ running: false, msg: `Cotejados ${data.total} · ${data.sin_coincidencia} limpios · ${data.con_coincidencia} con coincidencia${data.errores ? ` · ${data.errores} con error` : ''}.` })
      await load()
    } catch (e) {
      setBatch({ running: false, msg: e instanceof Error ? e.message : 'No pudimos correr el cotejo masivo.' })
    }
  }

  const doExport = async (fmt: 'pdf' | 'excel') => {
    setExporting(true)
    try {
      const config = await fetchConfigReporte()
      const { headers, data, subt } = exportData()
      const opts = {
        config, titulo: 'Bitácora de cotejo contra listas (OFAC / ONU)', subtitulo: subt,
        headers, rows: data, filename: 'bitacora-ofac', orientacion: 'landscape' as const,
      }
      if (fmt === 'pdf') await exportarPDF(opts); else await exportarExcel(opts)
    } finally { setExporting(false) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--n-900)' }}>Bitácora OFAC / listas</h2>
          <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 3 }}>
            Registro de cotejos contra SDN · NON-SDN · DPL · ONU — evidencia para IVE.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={batch.running} onClick={() => correrBatch('activos')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowClockwise size={15} weight="bold" /> {batch.running ? 'Cotejando…' : 'Re-cotejar activos'}
          </button>
          <button className="btn btn-ghost" disabled={batch.running} onClick={() => correrBatch('prospectos')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Prospectos
          </button>
          <span style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
          <button className="btn btn-ghost" disabled={exporting || !rows.length} onClick={() => doExport('excel')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MicrosoftExcelLogo size={15} weight="bold" /> Excel
          </button>
          <button className="btn btn-ghost" disabled={exporting || !rows.length} onClick={() => doExport('pdf')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FilePdf size={15} weight="bold" /> PDF
          </button>
        </div>
      </div>

      {batch.msg ? (
        <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 12.5,
          background: 'rgba(15,138,87,.08)', border: '1px solid rgba(15,138,87,.20)', color: 'var(--n-800)' }}>
          {batch.msg}
        </div>
      ) : null}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', padding: 14, border: '1px solid var(--border)', borderRadius: 12 }}>
        <Field label="Desde"><input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={inp} /></Field>
        <Field label="Hasta"><input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={inp} /></Field>
        <Field label="Resultado">
          <select value={resultado} onChange={(e) => setResultado(e.target.value)} style={inp}>
            <option value="">Todos</option>
            <option value="sin_coincidencia">Sin coincidencia</option>
            <option value="posible_coincidencia">Posible coincidencia</option>
            <option value="error">Error</option>
          </select>
        </Field>
        <Field label="Buscar (nombre o NeoID)">
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Ej. García / 223810" style={{ ...inp, minWidth: 220 }} />
        </Field>
        <button className="btn btn-ghost" onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ArrowClockwise size={14} weight="bold" /> Actualizar
        </button>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--n-500)' }}>
          <b style={{ color: 'var(--n-900)' }}>{stats.total}</b> consultas · {stats.limpias} limpias · <b style={{ color: stats.hits ? '#C0392B' : 'var(--n-500)' }}>{stats.hits}</b> con coincidencia
        </div>
      </div>

      {/* Tabla */}
      <div className="card-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th><th>Asociado</th><th>Nombre consultado</th><th>Listas</th>
              <th>Resultado</th><th style={{ textAlign: 'right' }}>Score</th><th>Decisión</th><th>Consultado por</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--n-500)' }}>Cargando…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--n-500)' }}>Sin consultas en el rango.</td></tr>
            ) : rows.map((r) => {
              const meta = RES[r.resultado] ?? RES.error
              return (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fechaHora(r.created_at)}</td>
                  <td>{r.neo_id ? <Link to={`/neocore/asociados?filtro=${r.neo_id}`} style={{ color: 'var(--brand-500)', textDecoration: 'none', fontFamily: 'ui-monospace, monospace' }}>{r.neo_id}</Link> : '—'}</td>
                  <td style={{ fontWeight: 500 }}>{r.nombre_consultado}</td>
                  <td style={{ fontSize: 11, color: 'var(--n-500)' }}>{(r.fuentes ?? []).join(' · ')}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: meta.color, fontWeight: 600, fontSize: 12 }}>
                      <meta.Icon size={14} weight="fill" /> {meta.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.score_max != null ? r.score_max : '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {r.decision ? <b style={{ color: r.decision === 'liberado' ? '#0F8A57' : '#C0392B' }}>{r.decision}</b>
                      : r.resultado === 'posible_coincidencia' ? <span style={{ color: '#C0392B' }}>pendiente</span> : '—'}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--n-600)' }}>{r.consultado_por_email ?? ORIGEN[r.origen] ?? r.origen}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const inp: React.CSSProperties = { padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: '#fff' }
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--n-500)', fontWeight: 600, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}
